import { useCookie } from '#app';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { getOriginalClient } from '~/components/supabaseClient';
import log from '~/server/utils/log';
import { createInitializationManager } from './utils/initializationManager';

// Create initialization manager for auth
const authInitManager = createInitializationManager('useAuth');

// Create shared reactive references
const _supabaseUser = ref(null);
const isLoading = ref(true);
let isInitialLoad = true; // Use a regular variable instead of ref
let authInitialized = false;
let showUidLoginPopup = ref(false);
let uidLoginEmail = ref('');
let uidLoginError = ref('');

// Add banner state for anonymous user
// const showAnonEmailBanner = ref(false); // Will be replaced by a computed property
// const anonUserEmail = ref(''); // Will be replaced by a computed property

const chatUserId = ref(null);
const chatUserRow = ref(null);
let userRowSubscription = null;

// Watcher cleanup function
let chatUserIdWatcherCleanup = null;

// Cache for anonymous user ID to prevent multiple cookie reads
let _cachedAnonUserId = null;
let _anonUserIdCookie = null;

// Simple UUID v4 generator
function generateUUID() {
  log.info('generating new anon_user_id');
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Shared cookie configuration for anon_user_id
const getAnonUserIdCookieConfig = () => {
  const runtimeConfig = useRuntimeConfig();
  const isProduction = runtimeConfig.public.ENV_TYPE === 'production';

  return {
    maxAge: 60 * 60 * 24 * 365, // 1 year
    sameSite: 'lax',
    secure: isProduction,
    httpOnly: false, // Need to access from client-side
    path: '/', // Explicit path
    default: () => null, // Explicit default value
  };
};

const getOrCreateAnonUserId = () => {
  // Return cached value if available
  if (_cachedAnonUserId) {
    log.info('🎯 [getOrCreateAnonUserId] Using cached anon_user_id:', _cachedAnonUserId);
    return _cachedAnonUserId;
  }

  log.info('🔍 [getOrCreateAnonUserId] No cache found, fetching from cookie...');

  // Initialize cookie instance only once
  if (!_anonUserIdCookie) {
    _anonUserIdCookie = useCookie('anon_user_id', getAnonUserIdCookieConfig());
  }

  // Always get or create anon_user_id from cookies - never clear it except via manual reset
  const cookie = _anonUserIdCookie;

  log.info('🍪 [getOrCreateAnonUserId] anon_user_id cookie value:', cookie.value);

  // Debug environment detection
  if (typeof window !== 'undefined') {
    const runtimeConfig = useRuntimeConfig();
    log.info(
      '🌍 [getOrCreateAnonUserId] Environment detection - ENV_TYPE:',
      runtimeConfig.public.ENV_TYPE,
      'env:',
      runtimeConfig.public.env,
    );
    log.info('🌍 [getOrCreateAnonUserId] Current URL:', window.location.href);
  }

  if (!cookie.value) {
    log.info('🔄 generating new anon_user_id - cookie was empty');
    const newId = generateUUID();
    cookie.value = newId;
    log.info('✅ generated anon_user_id:', newId);

    // Force cookie to be set immediately using multiple methods for reliability
    if (typeof window !== 'undefined') {
      // Method 1: Set via document.cookie as a fallback
      const runtimeConfig = useRuntimeConfig();
      const isProduction = runtimeConfig.public.ENV_TYPE === 'production';
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      const cookieString = `anon_user_id=${newId}; expires=${expires.toUTCString()}; path=/; SameSite=Lax${
        isProduction ? '; Secure' : ''
      }`;
      document.cookie = cookieString;
      log.info('📝 Set cookie via document.cookie:', cookieString);

      // Method 2: Also store in localStorage as a backup
      try {
        localStorage.setItem('anon_user_id_backup', newId);
        log.info('💾 Stored anon_user_id backup in localStorage');
      } catch (e) {
        console.warn('⚠️ Could not store anon_user_id backup in localStorage:', e);
      }

      // Method 3: Verify the cookie was set correctly
      setTimeout(() => {
        const testCookie = useCookie('anon_user_id', getAnonUserIdCookieConfig());
        log.info('🔍 Cookie verification after setting:', testCookie.value);
        if (testCookie.value !== newId) {
          console.error('❌ Cookie verification failed! Expected:', newId, 'Got:', testCookie.value);
        } else {
          log.info('✅ Cookie verification successful');
        }
      }, 100);
    }
  }

  // Double-check: if cookie is still empty, try to get from localStorage backup
  if (!cookie.value && typeof window !== 'undefined') {
    try {
      const backupId = localStorage.getItem('anon_user_id_backup');
      if (backupId) {
        log.info('🔄 Restoring anon_user_id from localStorage backup:', backupId);
        cookie.value = backupId;
        log.info('✅ Restored from backup, cookie now:', cookie.value);
      } else {
        log.info('📭 No localStorage backup found');
      }
    } catch (e) {
      console.warn('⚠️ Could not read anon_user_id backup from localStorage:', e);
    }
  }

  // Cache the result
  _cachedAnonUserId = cookie.value;
  return _cachedAnonUserId;
};

export function useAuth() {
  const route = useRoute();

  // Always use the same supabase instance
  const supabase = getOriginalClient();

  // Only update anon_user_id cookie from URL if we don't already have one
  // This prevents overriding existing anonymous user IDs on page refresh
  if (typeof window !== 'undefined' && route?.query?.userid) {
    // Use the same function to get the cookie to avoid multiple instances
    const currentAnonId = getOrCreateAnonUserId();

    // Only set the cookie if it doesn't exist yet (first visit with userid param)
    if (!currentAnonId) {
      const cookie = useCookie('anon_user_id', getAnonUserIdCookieConfig());
      cookie.value = route.query.userid;
    }
  }

  // Fetch email by user id (logincode)
  const getEmailByUserId = async (userId) => {
    try {
      log.info('getEmailByUserId', userId);
      const { data, error } = await supabase.schema('chat').from('users').select('email').eq('id', userId).single();
      log.info('getEmailByUserId', data, error);
      if (error || !data) return null;
      return data.email;
    } catch (e) {
      log.info('getEmailByUserId error', e);
      return null;
    }
  };

  // Utility to parse tokens from the URL hash
  function parseHashTokens() {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    const expires_in = params.get('expires_in');
    const token_type = params.get('token_type');
    return access_token && refresh_token ? { access_token, refresh_token, expires_in, token_type } : null;
  }

  // Utility to parse magic link verification URL parameters
  function parseMagicLinkParams() {
    if (typeof window === 'undefined') return null;
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const type = urlParams.get('type');
    const redirect_to = urlParams.get('redirect_to');
    return token && type === 'magiclink' ? { token, type, redirect_to } : null;
  }

  // Refresh the user state from Supabase
  const refreshUser = async () => {
    try {
      const {
        data: { user: latestUser },
      } = await supabase.auth.getUser();
      _supabaseUser.value = latestUser;
    } catch (e) {
      console.error('[useAuth] Failed to refresh user:', e);
    }
  };

  // Initialize Supabase client if not done already
  if (!supabase) {
    const runtimeConfig = useRuntimeConfig();
    supabase = createClient(runtimeConfig.public.SUPABASE_URL, runtimeConfig.public.SUPABASE_KEY);
  }

  // Add AI-specific routes to the list of public routes
  const publicRoutes = ['/ai/login', '/ai/signup', '/ai/password-recovery', '/ai'];

  // Initialize auth state
  const initAuth = async () => {
    return authInitManager.getOrCreatePromise(async () => {
      try {
        authInitialized = true;

        // Handle magic link verification first (takes precedence)
        const magicLinkResult = await handleMagicLinkCallback();
        if (magicLinkResult.success) {
          // Magic link was processed successfully, but we still need to complete initialization
          log.info('[initAuth] Magic link processed, completing initialization');
        } else if (magicLinkResult.reason !== 'no_magic_link_params') {
          // Magic link processing failed, but it was a magic link attempt
          console.error('[initAuth] Magic link processing failed:', magicLinkResult.error);
        } else {
          // No magic link params, check for OAuth tokens
          const tokens = parseHashTokens();
          if (tokens) {
            await supabase.auth.setSession({
              access_token: tokens.access_token,
              refresh_token: tokens.refresh_token,
            });

            // Handle OAuth callback for anonymous user linking
            await handleOAuthCallback();
          }
        }

        // // Get initial session
        let initialUser = null;
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();
          _supabaseUser.value = user;
          initialUser = user;
        } catch (e) {
          // Optionally log error, but do not alert
        }

        // Always check email confirmation status if logged in
        if (initialUser) {
          await checkAndUpdateEmailConfirmation(initialUser);
        }

        // // Set up auth state change listener only once
        // const {
        //   data: { subscription },
        // } = supabase.auth.onAuthStateChange(async (event, session) => {
        //   _supabaseUser.value = session?.user || null;

        //   // Always check email confirmation status on auth state change
        //   if (session?.user) {
        //     await checkAndUpdateEmailConfirmation(session.user);
        //   }

        //   // Handle OAuth sign-in events for anonymous user linking
        //   if (event === 'SIGNED_IN' && session?.user) {
        //     // Check if this is an OAuth sign-in by looking for provider metadata
        //     const isOAuthSignIn = session.user.app_metadata?.provider && session.user.app_metadata.provider !== 'email';

        //     if (isOAuthSignIn) {
        //       // Try to link anonymous user
        //       const anonId = getOrCreateAnonUserId();
        //       if (anonId) {
        //         await linkAnonymousUserToAuthenticatedUser(session.user.id, session.user.email, anonId);
        //       }
        //     }
        //   }
        // });

        // Set up chatUserId watcher only once
        if (!chatUserIdWatcherCleanup) {
          chatUserIdWatcherCleanup = watch(_supabaseUser, fetchChatUserId, { immediate: true });
        }

        // Set up watcher for chatUserId to fetch and subscribe to chat user row
        watch(chatUserId, fetchAndSubscribeChatUserRow, { immediate: true });

        // Handle login popup for userid or logincode in the URL
        const loginId = route.query.userid || route.query.logincode;

        if (loginId) {
          const email = await getEmailByUserId(loginId);
          if (email) {
            uidLoginEmail.value = email;
            showUidLoginPopup.value = true;
          } else {
            uidLoginError.value = 'User not found or email not available for this login code.';
          }
        }

        authInitManager.setInitialized(true);
      } catch (error) {
        console.error('Auth error:', error);
        authInitialized = false;
        authInitManager.reset();
        throw error;
      } finally {
        isLoading.value = false;
      }
    });
  };

  // Helper to check and update email confirmation status
  const checkAndUpdateEmailConfirmation = async (authUser) => {
    if (!authUser) return;
    // If user is not confirmed, show banner
    // The banner logic is now handled by computed properties `showAnonEmailBanner` and `anonUserEmail`
    // This function will now only focus on updating the database if necessary.
    if (!authUser.email_confirmed_at && !authUser.confirmed_at) {
      // Optionally update chat.users table
      await supabase.schema('chat').from('users').update({ email_confirmed: false }).eq('id', authUser.id);
    } else {
      // Optionally update chat.users table
      await supabase.schema('chat').from('users').update({ email_confirmed: true }).eq('id', authUser.id);
    }
  };

  // Login function
  const login = async (email, password, logoutFirst = false) => {
    try {
      // If logoutFirst is true, logout any existing user first
      if (logoutFirst) {
        await supabase.auth.signOut();
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Signup function
  const signUp = async (credentials) => {
    try {
      await supabase.auth.signOut();
      const { data, error } = await supabase.auth.signUp(credentials, {
        redirectTo: 'http://localhost:3000/agent',
      });
      if (error) throw error;
      // Link anonymous user if present
      if (data?.user?.id) {
        await linkAnonymousUserToAuthenticatedUser(data.user.id, credentials.email);
      }
      // Refresh user state after signup
      await refreshUser();
      return { data, error: null };
    } catch (error) {
      console.error('[signUp] Error in signUp:', error);
      return { data: null, error };
    }
  };

  // Google OAuth sign-in function
  const signInWithGoogle = async (options = {}) => {
    return await signInWithOAuth('google', options);
  };

  // Apple OAuth sign-in function
  const signInWithApple = async (options = {}) => {
    return await signInWithOAuth('apple', options);
  };

  // Generic OAuth sign-in function
  const signInWithOAuth = async (provider, options = {}) => {
    try {
      const { logoutFirst = false, redirectTo } = options;

      // If logoutFirst is true, logout any existing user first
      if (logoutFirst) {
        await supabase.auth.signOut();
      }

      // Store anonymous user ID before OAuth (in case we need to link later)
      const anonId = getOrCreateAnonUserId();

      // Get runtime config for site URL
      const runtimeConfig = useRuntimeConfig();

      // Use the public site URL from config, fallback to current location
      const defaultRedirectTo = runtimeConfig.public.siteURL
        ? `${runtimeConfig.public.siteURL}/agent`
        : typeof window !== 'undefined'
        ? `${window.location.origin}/agent`
        : undefined;

      // Configure OAuth options based on environment
      const oauthOptions = {
        redirectTo: redirectTo || defaultRedirectTo,
        queryParams: {
          // Pass anonymous user ID as a query parameter to handle linking after redirect
          anon_user_id: anonId,
        },
      };

      // For production, ensure we don't open a popup window
      // Supabase will redirect in the same window by default, but we can be explicit
      if (runtimeConfig.public.ENV_TYPE === 'production') {
        // skipBrowserRedirect: false ensures we redirect in the same window
        oauthOptions.skipBrowserRedirect = false;
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: oauthOptions,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error(
        `[signInWith${provider.charAt(0).toUpperCase() + provider.slice(1)}] Error in ${provider} OAuth:`,
        error,
      );
      return { data: null, error };
    }
  };

  // Handle magic link verification and redirect
  const handleMagicLinkCallback = async () => {
    try {
      // Check if we have magic link parameters in the URL
      const magicLinkParams = parseMagicLinkParams();
      if (!magicLinkParams) return { success: false, reason: 'no_magic_link_params' };

      log.info('[handleMagicLinkCallback] Processing magic link:', magicLinkParams);

      // Verify the magic link token with Supabase
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: magicLinkParams.token,
        type: 'magiclink',
      });

      if (error) {
        console.error('[handleMagicLinkCallback] Magic link verification failed:', error);
        throw error;
      }

      log.info('[handleMagicLinkCallback] Magic link verified successfully');

      // Get the authenticated user
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (authUser) {
        // Link anonymous user if present
        const anonId = getOrCreateAnonUserId();
        if (anonId) {
          await linkAnonymousUserToAuthenticatedUser(authUser.id, authUser.email, anonId);
        }
      }

      // Clean up URL parameters
      const url = new URL(window.location);
      url.searchParams.delete('token');
      url.searchParams.delete('type');
      url.searchParams.delete('redirect_to');

      // Determine redirect destination
      let redirectTo = '/agent'; // Default redirect
      if (magicLinkParams.redirect_to) {
        try {
          const redirectUrl = new URL(magicLinkParams.redirect_to);
          redirectTo = redirectUrl.pathname + redirectUrl.search + redirectUrl.hash;
        } catch (e) {
          // If redirect_to is not a valid URL, use it as a path
          redirectTo = magicLinkParams.redirect_to;
        }
      }

      log.info('[handleMagicLinkCallback] Redirecting to:', redirectTo);

      // Redirect to the intended destination
      if (typeof window !== 'undefined') {
        window.history.replaceState({}, document.title, url.toString());
        await navigateTo(redirectTo);
      }

      return { success: true, redirectTo };
    } catch (error) {
      console.error('[handleMagicLinkCallback] Error handling magic link callback:', error);
      return { success: false, error };
    }
  };

  // Handle OAuth callback and link anonymous user
  const handleOAuthCallback = async () => {
    try {
      // Check if we have OAuth tokens in the URL
      const tokens = parseHashTokens();
      if (!tokens) return;

      // Set the session
      await supabase.auth.setSession({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
      });

      // Get the authenticated user
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (authUser) {
        // Check if we have an anonymous user ID to link
        const urlParams = new URLSearchParams(window.location.search);
        const anonUserId = urlParams.get('anon_user_id') || getOrCreateAnonUserId();

        if (anonUserId) {
          await linkAnonymousUserToAuthenticatedUser(authUser.id, authUser.email, anonUserId);
        }
      }

      // Clean up URL
      window.location.hash = '';
      const url = new URL(window.location);
      url.searchParams.delete('anon_user_id');
      window.history.replaceState({}, document.title, url.toString());

      return { success: true };
    } catch (error) {
      console.error('[handleOAuthCallback] Error handling OAuth callback:', error);
      return { success: false, error };
    }
  };

  // Link anonymous user to authenticated user after signup/OAuth
  const linkAnonymousUserToAuthenticatedUser = async (newUserId, email, providedAnonId = null) => {
    if (typeof window === 'undefined') return;
    const anonId = providedAnonId || getOrCreateAnonUserId();
    if (!anonId) return;

    try {
      log.info('[linkAnonymousUser] Linking anonymous user:', anonId, 'to authenticated user:', newUserId);

      // 1. Check if the anonymous user exists
      const { data: anonUser, error } = await supabase
        .schema('chat')
        .from('users')
        .select('*')
        .eq('id', anonId)
        .single();

      if (anonUser) {
        log.info('[linkAnonymousUser] Found anonymous user, updating with auth info');
        // 2. Update the anonymous user record with the new auth user info
        await supabase
          .schema('chat')
          .from('users')
          .update({
            supabase_user_id: newUserId,
            email: email,
            status: 'authenticated',
            updated_at: new Date().toISOString(),
          })
          .eq('id', anonId);

        log.info('[linkAnonymousUser] Successfully linked anonymous user to authenticated user');
      } else {
        log.info('[linkAnonymousUser] Anonymous user not found, creating new chat user record');
        // If anonymous user not found, create a new chat.users record
        await supabase.schema('chat').from('users').insert({
          id: anonId,
          supabase_user_id: newUserId,
          email: email,
          status: 'authenticated',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
    } catch (e) {
      console.warn('[linkAnonymousUser] Could not link anonymous user to authenticated user:', e);
    }
  };

  // Save waitlist entry
  const saveWaitlistEntry = async (entry) => {
    try {
      const { data, error } = await supabase.from('waitlist').insert(entry);
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Password recovery request
  const resetPasswordForEmail = async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Update password
  const updatePassword = async (password) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      log.info('🚪 [logout] Starting logout process');
      const { error } = await supabase.auth.signOut();

      // Never automatically clear the anonymous user ID on logout
      // The anon_user_id should always persist across sessions
      // It can only be manually cleared using the resetAnonUserId function in development

      // Get the existing anon user id (should always exist)
      const anonId = getOrCreateAnonUserId();
      log.info('🚪 [logout] Anon user ID after logout:', anonId);

      // Clear the email for the anon user in chat.users (but keep the user record)
      try {
        await supabase
          .schema('chat')
          .from('users')
          .update({
            email: '',
            supabase_user_id: null,
            status: 'anonymous',
          })
          .eq('id', anonId);
      } catch (e) {
        // Ignore errors here
      }

      if (error) throw error;

      // Reload the page to ensure all state is properly reset
      if (typeof window !== 'undefined') {
        log.info('[logout] Reloading page to reset state');
        window.location.reload();
      }

      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  // Check if user has access to AI features (approved waitlist status)
  const checkUserAccess = async (userId) => {
    try {
      const { data, error } = await supabase.from('waitlist').select('status').eq('user_id', userId).single();

      if (error) {
        if (error.code === 'PGRST116') {
          return { hasAccess: false, status: 'not_found' };
        }
        throw error;
      }

      return {
        hasAccess: data.status === 'approved',
        status: data.status,
      };
    } catch (error) {
      console.error('Error checking user access:', error);
      return { hasAccess: false, status: 'error' };
    }
  };

  // Computed user: supabase user or anonymous user fallback
  const user = computed(() => {
    const isAuthUser = !!_supabaseUser.value;
    const baseUser = {
      id: isAuthUser ? _supabaseUser.value.id : _cachedAnonUserId || getOrCreateAnonUserId(),
      email: isAuthUser ? _supabaseUser.value.email : '',
      isAnonymous: !isAuthUser,
      emailConfirmed: isAuthUser
        ? !!(_supabaseUser.value.email_confirmed_at || _supabaseUser.value.confirmed_at)
        : false,
      emailSentAt: null, // Default, will be overridden by chatUserRow if available
      // Spread other relevant fields from _supabaseUser if authenticated
      ...(isAuthUser ? _supabaseUser.value : {}),
    };

    // Merge data from chatUserRow, giving it precedence for custom fields
    // and specific fields like emailConfirmed and emailSentAt for anonymous users
    if (chatUserRow.value) {
      const chatData = { ...chatUserRow.value };

      // Ensure chatUserRow doesn't override the canonical ID and isAnonymous status
      delete chatData.id;
      delete chatData.isAnonymous;

      // If anonymous and chatUserRow has an email, use it.
      if (baseUser.isAnonymous && chatData.email) {
        baseUser.email = chatData.email;
      }

      // Override emailConfirmed and emailSentAt with values from chatUserRow if present
      if (typeof chatData.email_confirmed === 'boolean') {
        baseUser.emailConfirmed = chatData.email_confirmed;
      }
      if (chatData.email_sent_at) {
        baseUser.emailSentAt = chatData.email_sent_at;
      }

      // Merge remaining chatData fields
      // Filter out fields already handled or that should not be overridden
      const fieldsToExclude = [
        'id',
        'email',
        'isAnonymous',
        'emailConfirmed',
        'emailSentAt',
        'supabase_user_id',
        'email_confirmed',
        'email_sent_at',
      ];
      const additionalChatData = Object.fromEntries(
        Object.entries(chatData).filter(([key]) => !fieldsToExclude.includes(key)),
      );

      return {
        ...baseUser,
        ...additionalChatData,
      };
    }

    // If user is anonymous and route.query.userid exists, use it as the id
    // This handles the case where anonUserInfo was previously used.
    if (baseUser.isAnonymous && route?.query?.userid && baseUser.id !== route.query.userid) {
      baseUser.id = route.query.userid;
    }

    return baseUser;
  });

  const fetchChatUserId = async () => {
    if (_supabaseUser.value && _supabaseUser.value.id) {
      // User is logged in, fetch chat.users row by supabase_user_id
      const { data, error } = await supabase
        .schema('chat')
        .from('users')
        .select('id')
        .eq('supabase_user_id', _supabaseUser.value.id)
        .single();
      log.debug('fetchChatUserId', data, error);
      if (data && data.id) {
        chatUserId.value = data.id;
      } else {
        // Fallback: use supabase user id (should only happen if chat.users row not created yet)
        chatUserId.value = _supabaseUser.value.id;
      }
    } else {
      // Not logged in, use anon_user_id from cookies (or create one)
      chatUserId.value = _cachedAnonUserId || getOrCreateAnonUserId();
    }
  };

  // Fetch the chat.users row and set up realtime updates
  const fetchAndSubscribeChatUserRow = async () => {
    if (!chatUserId.value) {
      chatUserRow.value = null;
      return;
    }

    // Prevent multiple simultaneous calls
    if (userRowSubscription) {
      return;
    }

    // Fetch the user row
    try {
      const { data, error } = await supabase
        .schema('chat')
        .from('users')
        .select('*')
        .eq('id', chatUserId.value)
        .single();
      if (error) {
        chatUserRow.value = null;
      } else {
        chatUserRow.value = data;
      }
    } catch (e) {
      chatUserRow.value = null;
    }

    // Subscribe to updates for this user row
    userRowSubscription = supabase
      .channel('user_row_updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'chat',
          table: 'users',
          filter: `id=eq.${chatUserId.value}`,
        },
        (payload) => {
          if (payload.new) {
            log.info('[CHAT LISTENER] User status changed', payload.new.status);
            chatUserRow.value = payload.new;
          }
        },
      )
      .subscribe();
  };

  // Computed properties for anonymous user email banner state
  const anonUserEmail = computed(() => {
    if (!_supabaseUser.value && chatUserRow.value?.email) {
      return chatUserRow.value.email;
    }
    return '';
  });

  const showAnonEmailBanner = computed(() => {
    const currentUser = user.value;
    if (
      currentUser &&
      currentUser.isAnonymous &&
      currentUser.email &&
      currentUser.emailSentAt &&
      !currentUser.emailConfirmed
    ) {
      return true;
    }
    return false;
  });

  // Computed property for user status from chat system
  const userStatus = computed(() => {
    // If we have a chat user row with status, use that
    if (chatUserRow.value?.status) {
      return chatUserRow.value.status;
    }

    // Fallback logic based on authentication state
    if (_supabaseUser.value) {
      return 'authenticated';
    }

    // Default to anonymous for unauthenticated users
    return 'anonymous';
  });

  // Clean up on composable unmount or page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (userRowSubscription) {
        supabase.removeChannel(userRowSubscription);
        userRowSubscription = null;
      }
      if (chatUserIdWatcherCleanup) {
        chatUserIdWatcherCleanup();
        chatUserIdWatcherCleanup = null;
      }
    });
  }

  // Initialize auth when the composable is used
  initAuth();

  // Clear UID login popup state
  const clearUidLoginState = () => {
    showUidLoginPopup.value = false;
    uidLoginEmail.value = '';
    uidLoginError.value = '';
  };

  // Clear UID login error
  const clearUidLoginError = () => {
    uidLoginError.value = '';
  };

  // Magic link login (passwordless)
  const loginWithMagicLink = async (email, options = {}) => {
    try {
      const { redirectTo } = options;
      const runtimeConfig = useRuntimeConfig();

      // Use the public site URL from config, fallback to current origin + /ai
      const defaultRedirectTo = runtimeConfig.public.siteURL
        ? `${runtimeConfig.public.siteURL}/agent`
        : typeof window !== 'undefined'
        ? `${window.location.origin}/agent`
        : '/agent';

      log.info('Magic link redirect URL:', defaultRedirectTo);
      log.info('Magic link redirect URL:', redirectTo);

      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectTo || defaultRedirectTo,
        },
      });

      if (error) {
        console.error('Magic link error:', error);

        // Handle specific error cases with user-friendly messages
        let userFriendlyMessage = error.message;

        if (error.message?.toLowerCase().includes('too many requests') || error.status === 429) {
          userFriendlyMessage = 'Too many requests. Please wait a few minutes before trying again.';
        } else if (error.message?.toLowerCase().includes('rate limit')) {
          userFriendlyMessage = 'Rate limit exceeded. Please wait before requesting another magic link.';
        } else if (error.message?.toLowerCase().includes('invalid email')) {
          userFriendlyMessage = 'Please enter a valid email address.';
        } else if (error.message?.toLowerCase().includes('email not confirmed')) {
          userFriendlyMessage = 'Please check your email and click the confirmation link first.';
        }

        throw { ...error, message: userFriendlyMessage };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Magic link error:', error);
      return { data: null, error };
    }
  };

  // Reset anonymous user ID (for development/testing only)
  const resetAnonUserId = async () => {
    try {
      // Only allow in development/debug mode
      const runtimeConfig = useRuntimeConfig();
      const isDebugMode = runtimeConfig.public.ENV_TYPE !== 'production';

      log.info('🔄 [resetAnonUserId] Called - ENV_TYPE:', runtimeConfig.public.ENV_TYPE, 'isDebugMode:', isDebugMode);

      if (!isDebugMode) {
        console.warn('🚫 [resetAnonUserId] Only available in development mode (ENV_TYPE !== "production")');
        return null;
      }

      log.info('🔄 [resetAnonUserId] Resetting anonymous user ID...');

      // Clear the cache first
      _cachedAnonUserId = null;

      // Clear the cookie
      if (!_anonUserIdCookie) {
        _anonUserIdCookie = useCookie('anon_user_id', getAnonUserIdCookieConfig());
      }
      _anonUserIdCookie.value = null;

      // Clear localStorage as well (used by useChat)
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem('anon_user_id');
        localStorage.removeItem('anon_user_id_backup');
      }

      // Generate new anonymous user ID
      const newAnonId = getOrCreateAnonUserId();

      // Update chatUserId to the new ID
      chatUserId.value = newAnonId;

      // Clear the chat user row since we have a new ID
      chatUserRow.value = null;

      log.info('✅ Anonymous user ID reset to:', newAnonId);

      // Refresh the page to ensure all components use the new ID
      if (typeof window !== 'undefined') {
        window.location.reload();
      }

      return newAnonId;
    } catch (error) {
      console.error('❌ Error resetting anonymous user ID:', error);
      throw error;
    }
  };

  // Create a cached function for anonUid to prevent repeated calls
  const anonUid = () => {
    return _cachedAnonUserId || getOrCreateAnonUserId();
  };

  return {
    user,
    chatUserId,
    chatUserRow,
    isLoading,
    initAuth,
    login,
    signUp,
    saveWaitlistEntry,
    resetPasswordForEmail,
    updatePassword,
    logout,
    checkUserAccess,
    // Add these for anonymous user banner
    showAnonEmailBanner,
    anonUserEmail,
    getOrCreateAnonUserId,
    anonUid,
    refreshUser,
    // For UID login popup
    showUidLoginPopup,
    uidLoginEmail,
    uidLoginError,
    clearUidLoginState,
    clearUidLoginError,
    signInWithGoogle,
    signInWithApple,
    handleOAuthCallback,
    handleMagicLinkCallback,
    // Computed property for user status from chat system
    userStatus,
    // Add magic link login
    loginWithMagicLink,
    // Development/testing functions
    resetAnonUserId,
  };
}
